import { useContext, useEffect, useState } from 'react';
import { useHref } from 'react-router-dom';
import { DateTime, DurationLike } from 'luxon';

import { BUILD_NUMBER } from '../../../constants';

import config from '../../../config';
import { clearTokens } from '../../basic/auth/Auth.hooks';
import { AppContext } from '../../../App.context';
import { Logger } from '../../../utils/logger';
import {
	ACCESS_TOKEN_EXPIRATION_KEY,
	ACCESS_TOKEN_KEY,
	IS_AUTHENTICATED_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_KEY,
	USER_ROLE,
} from '@/auth/constants';

export const clientId = '1qajn2tivh7ee4abd4lf531j8o';
export const cognitoUrl = 'https://auth.ami-admin.com';

const logger = new Logger('Token', {
	log: 'rgb(88, 86, 214)',
	debug: 'rgb(54, 52, 163)',
	info: 'rgb(125, 122, 255)',
	error: 'rgb(211, 15, 69)',
});

export function useAuth(): AuthResponse {
	const basename = useHref('/callback');
	const isAuthenticated =
		localStorage.getItem(IS_AUTHENTICATED_KEY) === 'true';
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const [isLoading, setIsLoading] = useState(true);

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const redirectSections = [
		`client_id=${clientId}`,
		'response_type=code',
		'scope=email+openid+profile',
		`redirect_uri=${redirectUri}`,
	].join('&');

	const redirectSignUpUrl = [
		cognitoUrl,
		'/authorize?',
		redirectSections,
	].join('');

	useEffect(() => {
		if (isAdmin) {
			setIsLoading(false);
			return;
		}

		if (!isAuthenticated) {
			console.log(`I should redirect to this page: ${redirectSignUpUrl}`);
			location.href = redirectSignUpUrl;
			setIsLoading(false);
			return;
		}

		// At this point we know that the user is authenticated
		const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		const accessTokenExp = localStorage.getItem(
			ACCESS_TOKEN_EXPIRATION_KEY,
		);
		const refreshTokenExp = localStorage.getItem(
			REFRESH_TOKEN_EXPIRATION_KEY,
		);

		// If one of this two tokens are missing, clear everything and redirect
		if (
			!accessToken ||
			!refreshToken ||
			!accessTokenExp ||
			!refreshTokenExp
		) {
			clearTokens(redirectSignUpUrl);
			setIsLoading(false);
			return;
		}

		// In here the access token is expired but the app is offline
		if (!navigator.onLine) {
			setIsLoading(false);
			return;
		}

		// JWT Parse Data

		// Calculate expiration dates
		const accessExpirationDate = DateTime.fromISO(accessTokenExp);
		const refreshExpirationDate = DateTime.fromISO(refreshTokenExp);

		console.log(`ACCESS TOKEN EXPIRATION DATE:`, accessExpirationDate);
		console.log(`REFRESH TOKEN EXPIRATION DATE:`, refreshExpirationDate);

		const now = DateTime.now();

		// If now is larger than refresh expiration date clear the tokens
		if (now >= refreshExpirationDate) {
			clearTokens(redirectSignUpUrl);
			setIsLoading(false);
			return;
		}

		// If true the user access token is valid
		if (now < accessExpirationDate) {
			setIsLoading(false);
			return;
		}

		refreshAccessToken(refreshToken)
			.then(accessExpirationDate => {
				setIsLoading(false);
				logger.log(
					`Token refreshed successfully: '${accessExpirationDate}'`,
				);
			})
			.catch(err => {
				logger.error(err);
				// alert(err.message);
				clearTokens(redirectSignUpUrl);
				setIsLoading(false);
			});
	}, []);
	return { isAuthenticated, isLoading };
}

interface AuthResponse {
	isLoading: boolean;
	isAuthenticated: boolean;
}

export function tryRefreshToken(
	redirect: string,
	durationOffset?: DurationLike,
) {
	const isAuthenticated = localStorage.getItem(IS_AUTHENTICATED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return;
	}
	if (!isAuthenticated) {
		return;
	}

	// At this point we know that the user is authenticated
	const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
	const accessTokenExp = localStorage.getItem(ACCESS_TOKEN_EXPIRATION_KEY);
	const refreshTokenExp = localStorage.getItem(REFRESH_TOKEN_EXPIRATION_KEY);

	// In here the access token is expired but the app is offline
	if (!navigator.onLine) {
		return;
	}

	// If one of this two tokens are missing, clear everything and redirect
	if (!accessToken || !refreshToken || !accessTokenExp || !refreshTokenExp) {
		logger.debug(
			`One of the two tokens is missing and i will kick you out`,
		);
		clearTokens(redirect);
		return;
	}

	// Calculate expiration dates
	const accessExpirationDate = durationOffset
		? DateTime.fromISO(accessTokenExp).minus(durationOffset)
		: DateTime.fromISO(accessTokenExp);
	const refreshExpirationDate = DateTime.fromISO(refreshTokenExp);

	const now = DateTime.now();

	// If now is larger than refresh expiration date clear the tokens
	if (now >= refreshExpirationDate) {
		logger.debug(`THE REDIRECT TOKEN EXPIRED AND I WILL KICK YOU OUT`);
		clearTokens(redirect);
		return;
	}

	// If true the user access token is valid
	if (now < accessExpirationDate) {
		const diff = accessExpirationDate.diff(now);
		logger.debug(
			`The token is valid and is going to expire in `,
			msToTime(diff.toObject().milliseconds),
		);
		return;
	}

	refreshAccessToken(refreshToken)
		.then(accessExpirationDate => {
			const accessExpiration = DateTime.fromJSDate(
				new Date(accessExpirationDate),
			);
			const now = DateTime.now();
			const diff = accessExpiration.diff(now);
			logger.log(`Token refreshed successfully`);
			logger.debug(
				`Token is going to expire at: ${accessExpirationDate}`,
			);

			logger.debug(
				`The token is going to expire in: ${msToTime(
					diff.toObject().milliseconds,
				)}`,
			);
		})
		.catch(err => {
			// THERE IS AN ERROR
			logger.error(err);
			clearTokens(redirect);
		});
}

export function logOut(basename: string) {
	const isAuthenticated = localStorage.getItem(IS_AUTHENTICATED_KEY);
	if (!isAuthenticated) {
		return;
	}

	location.href = '/logout';

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const redirectSections = [
		`client_id=${clientId}`,
		`logout_uri=${redirectUri}`,
		`redirect_uri=${redirectUri}`,
		`response_type=code`,
	].join('&');

	const redirectUrl = [cognitoUrl, '/logout?', redirectSections].join('');

	location.href = redirectUrl;
}

export function useLogout(): boolean {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { kv, rebuildGraphQLClient } = useContext(AppContext);
	useEffect(() => {
		kv.clear();
		clearTokens();

		rebuildGraphQLClient();
		setIsLoading(false);
	}, []);

	return isLoading;
}

/**
 * React hook that reset everything if the build changes
 *
 *
 */
export function useRefreshOnBuild() {
	useEffect(() => {
		const buildNumber = localStorage.getItem(BUILD_NUMBER);
		if (!buildNumber) {
			localStorage.setItem(BUILD_NUMBER, `${config.build.buildNumber}`);
			clearTokens();
			location.reload();
			return;
		}
		if (buildNumber !== `${config.build.buildNumber}`) {
			console.log(`This build is different than the existing one`);
			localStorage.setItem(BUILD_NUMBER, `${config.build.buildNumber}`);
			clearTokens();
			location.reload();
			return;
		}
		console.log(`The app has the same build`);
	}, []);
}

async function refreshAccessToken(token: string): Promise<string> {
	const queryParams = new URLSearchParams({
		refresh_token: token,
	}).toString();
	const url = `${config.build.host}/a360/magshop/token?${queryParams}`;
	const rawResponse = await fetch(url, {
		method: 'GET',
		headers: {
			'app-key': config.build.appKey,
			'Content-Type': 'application/json',
		},
	});
	const content = await rawResponse.json();
	const { access_token, expires } = content;

	// Calculate expiration dates
	const accessExpirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();

	localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, accessExpirationDate);
	return accessExpirationDate;
}

function msToTime(duration?: number): string {
	if (!duration) {
		return '';
	}
	const milliseconds: any = Math.floor((duration % 1000) / 100);
	let seconds: number | string = Math.floor((duration / 1000) % 60);
	let minutes: number | string = Math.floor((duration / (1000 * 60)) % 60);
	let hours: number | string = Math.floor((duration / (1000 * 60 * 60)) % 24);

	hours = hours < 10 ? '0' + hours : hours;
	minutes = minutes < 10 ? '0' + minutes : minutes;
	seconds = seconds < 10 ? '0' + seconds : seconds;

	return hours + ':' + minutes + ':' + seconds + '.' + milliseconds;
}
